import { supabase } from "@/integrations/supabase/client";
import type {
  DataSubjectRequest,
  PrivacyExportPayload,
  PrivacyLogItem,
  PrivacyLogEventType,
  PrivacyRequestStatus,
  PrivacyRequestType,
} from "@/types/services/api-contracts";

import { callEdgeData, callPublicEdgeData } from "./_core/edge-call";
import { PRIVACY_FUNCTION_NAME } from "./_core/function-names";

export const privacyService = {
  async createRequest(input: {
    requester_name: string;
    requester_email: string;
    request_type: PrivacyRequestType;
    description: string;
  }): Promise<DataSubjectRequest> {
    const { data } = await supabase.auth.getSession();
    return data.session
      ? callEdgeData<DataSubjectRequest>(PRIVACY_FUNCTION_NAME, "create-request", { body: input })
      : callPublicEdgeData<DataSubjectRequest>(PRIVACY_FUNCTION_NAME, "create-request", { body: input });
  },

  async listMyRequests(): Promise<DataSubjectRequest[]> {
    return callEdgeData<DataSubjectRequest[]>(PRIVACY_FUNCTION_NAME, "list-my-requests", {
      method: "GET",
    });
  },

  async logConsentChange(input: {
    event_type: Extract<PrivacyLogEventType, "consent_granted" | "consent_revoked">;
    categories: Record<string, boolean>;
  }): Promise<{ success: boolean }> {
    return callEdgeData<{ success: boolean }>(PRIVACY_FUNCTION_NAME, "log-consent-change", {
      body: input,
    });
  },

  async listAdminRequests(filters: {
    status?: PrivacyRequestStatus;
    request_type?: PrivacyRequestType;
  } = {}): Promise<DataSubjectRequest[]> {
    return callEdgeData<DataSubjectRequest[]>(PRIVACY_FUNCTION_NAME, "admin-list-requests", {
      method: "GET",
      params: Object.fromEntries(
        Object.entries(filters).filter(([, value]) => typeof value === "string" && value.length > 0),
      ),
    });
  },

  async reviewAdminRequest(input: {
    request_id: string;
    status: Exclude<PrivacyRequestStatus, "open">;
    admin_notes: string;
    identity_verified: boolean;
  }): Promise<DataSubjectRequest> {
    return callEdgeData<DataSubjectRequest>(PRIVACY_FUNCTION_NAME, "admin-review-request", {
      body: input,
    });
  },

  async exportAdminRequest(requestId: string, reason: string): Promise<PrivacyExportPayload> {
    return callEdgeData<PrivacyExportPayload>(PRIVACY_FUNCTION_NAME, "admin-export-request", {
      body: { request_id: requestId, reason },
    });
  },

  async anonymizeAdminRequest(requestId: string, reason: string): Promise<DataSubjectRequest> {
    return callEdgeData<DataSubjectRequest>(PRIVACY_FUNCTION_NAME, "admin-anonymize-request", {
      body: { request_id: requestId, reason },
    });
  },

  async listAdminLogs(filters: { request_id?: string } = {}): Promise<PrivacyLogItem[]> {
    return callEdgeData<PrivacyLogItem[]>(PRIVACY_FUNCTION_NAME, "admin-list-logs", {
      method: "GET",
      params: Object.fromEntries(
        Object.entries(filters).filter(([, value]) => typeof value === "string" && value.length > 0),
      ),
    });
  },
};
